// src/ApiPanel/routes/apiPanelRoutes.jsx
// Mount this inside your top-level <Routes> tree — it is fully
// self-contained: its own session provider, its own auth guard, its own
// login page. It shares no component, context, or storage key with the
// main app's <AuthorisedUser> tree.
//
// Usage in your root router (e.g. App.jsx):
//
//   import { apiPanelRouteElements } from "ApiPanel/routes/apiPanelRoutes";
//
//   <Routes>
//     {/* ...your existing routes... */}
//     {apiPanelRouteElements}
//   </Routes>
//
// Add it as a SIBLING of your AuthorisedUser route block, not nested inside
// it — nesting would run the main site's auth/redirect logic before the API
// Panel's own guard ever gets a chance to run.
import React from "react";
import { Route, Outlet } from "react-router";
import { ApiPanelSessionProvider } from "../context/ApiPanelSessionContext";
import ApiPanelRequireAuth from "../components/ApiPanelRequireAuth";
import ApiPanelLayout from "../components/ApiPanelLayout";
import ApiPanelLogin from "../pages/ApiPanelLogin";
import ApiPanelDashboard from "../pages/ApiPanelDashboard";
import ApiPanelComingSoon from "../pages/ApiPanelComingSoon";
import ApiPanelTokenManager from "../pages/ApiPanelTokenManager";
import ComplaintCenter from "../pages/ComplaintCenter";

// CA Report
import MonthlyLedger from "../pages/ca/MonthlyLedger";
import TdsReport from "../pages/ca/TdsReport";
import CashDeclaration from "../pages/ca/CashDeclaration";
import {
  PurchaseInvoiceDetails,
  PurchaseRechargeInvoiceDetails,
  PurchaseUtilityInvoiceDetails,
  SellCmsInvoiceDetails,
  SellInvoiceDetails,
  SellRechargeInvoiceDetails,
} from "../pages/ca/InvoicePages";
import {
  UploadCashDeclaration,
  UploadGst,
  UploadGstRecharge,
} from "../pages/ca/UploadPages";

// Transaction Report
import {
  AadharVerifyReport, AepsReport, AxisAccountOpen, BillPaymentDraftReport,
  BillPaymentReport, CashDepositReport, CreditDebitReport, DmtReport,
  FinoCmsReport, ManualPancardRequest, MatmReport, MoneyFailedToSucc,
  MoneySuccToFailed, NortonReport, PanCardTxnHistory, PanVerifyReport,
  PayoutReport, RechargeDispute, RechargeDthReport, UpiQrReport,
} from "../pages/txn/TransactionPages";

// Token & API Request
import ApiIntegrationRequest from "../pages/api/ApiIntegrationRequest";

// GST Report
import GstInformation from "../pages/gst/GstInformation";
import GstPayReport from "../pages/gst/GstPayReport";
import GstReversalReport from "../pages/gst/GstReversalReport";

import { flattenNavLeaves } from "../constants/apiPanelNav";

// Root of the /api-panel subtree: mounts the isolated session provider once,
// above both the login page and the authenticated layout, then renders
// whichever child route matched.
function ApiPanelRoot() {
  return (
    <ApiPanelSessionProvider>
      <Outlet />
    </ApiPanelSessionProvider>
  );
}

// Paths that have a real page component instead of the generic stub.
// Everything in the nav that is not listed here still gets an automatic
// "coming soon" route, so adding a nav item never produces a dead link.
const REAL_PAGES = {
  "/api-panel/complaint": ComplaintCenter,

  // ---- Token & API Request ----
  "/api-panel/tokens/manage": ApiPanelTokenManager,
  "/api-panel/tokens/integration-request": ApiIntegrationRequest,

  // ---- Transaction Report: backed by real endpoints ----
  "/api-panel/reports/transactions/prepaid-dth": RechargeDthReport,
  "/api-panel/reports/transactions/bill-payment": BillPaymentReport,
  "/api-panel/reports/transactions/bill-payment-draft": BillPaymentDraftReport,
  "/api-panel/reports/transactions/aeps": AepsReport,
  "/api-panel/reports/transactions/matm": MatmReport,
  "/api-panel/reports/transactions/dmt": DmtReport,
  "/api-panel/reports/transactions/upi-qr": UpiQrReport,
  "/api-panel/reports/transactions/credit-debit": CreditDebitReport,
  "/api-panel/reports/transactions/pancard-txn-history": PanCardTxnHistory,

  // ---- Transaction Report: in the nav, no endpoint behind them ----
  "/api-panel/reports/transactions/payout": PayoutReport,
  "/api-panel/reports/transactions/recharge-dispute": RechargeDispute,
  "/api-panel/reports/transactions/cash-deposit": CashDepositReport,
  "/api-panel/reports/transactions/manual-pancard-request": ManualPancardRequest,
  "/api-panel/reports/transactions/money-succ-to-failed": MoneySuccToFailed,
  "/api-panel/reports/transactions/money-failed-to-succ": MoneyFailedToSucc,
  "/api-panel/reports/transactions/aadhar-verify": AadharVerifyReport,
  "/api-panel/reports/transactions/pan-verify": PanVerifyReport,
  "/api-panel/reports/transactions/axis-account-open": AxisAccountOpen,
  "/api-panel/reports/transactions/kotak-qr": UpiQrReport,
  "/api-panel/reports/transactions/norton": NortonReport,
  "/api-panel/reports/transactions/fino-cms": FinoCmsReport,

  // ---- CA Report ----
  "/api-panel/ca-report/monthly-ledger": MonthlyLedger,
  "/api-panel/ca-report/sell-cms-invoice-details": SellCmsInvoiceDetails,
  "/api-panel/ca-report/sell-invoice-details": SellInvoiceDetails,
  "/api-panel/ca-report/purchase-invoice-details": PurchaseInvoiceDetails,
  "/api-panel/ca-report/sell-recharge-invoice-details": SellRechargeInvoiceDetails,
  "/api-panel/ca-report/purchase-recharge-invoice-details": PurchaseRechargeInvoiceDetails,
  "/api-panel/ca-report/purchase-utility-invoice-details": PurchaseUtilityInvoiceDetails,
  "/api-panel/ca-report/tds": TdsReport,
  "/api-panel/ca-report/cash-declaration": CashDeclaration,
  "/api-panel/ca-report/upload-gst": UploadGst,
  "/api-panel/ca-report/upload-gst-recharge": UploadGstRecharge,
  "/api-panel/ca-report/upload-cash-declaration": UploadCashDeclaration,

  // ---- GST Report ----
  "/api-panel/gst-report/information": GstInformation,
  "/api-panel/gst-report/pay-report": GstPayReport,
  "/api-panel/gst-report/reversal-report": GstReversalReport,
};

const stubItems = flattenNavLeaves().filter(
  (item) => item.path !== "/api-panel/dashboard" && !REAL_PAGES[item.path]
);

const rel = (path) => path.replace("/api-panel/", "");

export const apiPanelRouteElements = (
  <Route path="/api-panel" element={<ApiPanelRoot />}>
    <Route path="login" element={<ApiPanelLogin />} />

    <Route
      element={
        <ApiPanelRequireAuth>
          <ApiPanelLayout />
        </ApiPanelRequireAuth>
      }
    >
      <Route index element={<ApiPanelDashboard />} />
      <Route path="dashboard" element={<ApiPanelDashboard />} />

      {Object.entries(REAL_PAGES).map(([path, Component]) => (
        <Route key={path} path={rel(path)} element={<Component />} />
      ))}

      {stubItems.map((item) => (
        <Route
          key={item.key}
          path={rel(item.path)}
          element={<ApiPanelComingSoon title={item.label} />}
        />
      ))}
    </Route>
  </Route>
);
